import PropTypes from "prop-types"
import React, { useEffect } from "react"

import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom"
import { connect, useDispatch, useSelector } from "react-redux"

// Import Routes all
import {
  userRoutes,
  authRoutes,
  publicRoutes,
  onboardingRoutes,
  adminRoutes,
  cityPortalLegacyRoutes,
} from "./routes/allRoutes"

// Import all middleware
import Authmiddleware from "./routes/middleware/Authmiddleware"
import AdminMiddleware from "./routes/middleware/AdminMiddleware"
import GuestAuthMiddleware from "./routes/middleware/GuestAuthMiddleware"
import OnboardingMiddleware from "./routes/middleware/OnboardingMiddleware"
import MemberMiddleware from "./routes/middleware/MemberMiddleware"

// layouts Format
import VerticalLayout from "./components/VerticalLayout/"
import HorizontalLayout from "./components/HorizontalLayout/"
import Logout from "./pages/Authentication/Logout"
import NonAuthLayout from "./components/NonAuthLayout"
import AdminLayout from "./components/AdminLayout"

// Import scss
import "./assets/scss/theme.scss"

import { getToken, onMessage } from "firebase/messaging"
import { messaging } from "./constants/firebase/firebaseConfig"

import { toast } from "react-toastify"
import Message from "./components/Common/MessageNotification"
import { setNotificaiton } from "./store/slices/authSlice"
import { bumpNotificationPulse } from "./store/slices/notificationsSlice"
import { getAuthenticatedHomePath, isOnboardingPath } from "./helpers/routeAccess"
import {
  openNotificationUrl,
  refreshNotificationInbox,
} from "./helpers/notifications"
import {
  isLevelUpNotification,
  maybeCelebrateLevelUpFromNotification,
} from "./helpers/xpLevelCelebration"
import { subscribeNetworkStatusToasts } from "./helpers/networkError"
import DocumentTitle from "./components/Common/DocumentTitle"
import { CreateFlagModalProvider } from "./components/Modals/CreateFlagModal"
import { AddOngoingRoleModalProvider } from "./components/Modals/AddOngoingRoleModal"
import { FloatingPlayerProvider } from "./components/FloatingPlayer"
import { ChatDockProvider } from "./components/DirectChat"

const HomeRedirect = () => {
  const currentUser = useSelector(state => state.auth.currentUser)
  const location = useLocation()
  // Keep ?dm= / ?context_id= (and other query) when bouncing `/` → home.
  return (
    <Navigate
      to={{
        pathname: getAuthenticatedHomePath(currentUser),
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  )
}

const renderNestedRoutes = (children, keyPrefix = "") =>
  children?.map((child, childIdx) => {
    const renderNested = nestedChildren =>
      nestedChildren?.map((nested, nestedIdx) =>
        nested.index ? (
          <Route
            index
            element={nested.component}
            key={`${keyPrefix}nested-idx-${
              child.path || childIdx
            }-${nestedIdx}`}
          />
        ) : (
          <Route
            path={nested.path}
            element={nested.component}
            key={
              nested.path ||
              `${keyPrefix}nested-${child.path || childIdx}-${nestedIdx}`
            }
          >
            {renderNested(nested.children)}
          </Route>
        )
      )

    return child.index ? (
      <Route
        index
        element={child.component}
        key={`${keyPrefix}idx-${childIdx}`}
      />
    ) : (
      <Route
        path={child.path}
        element={child.component}
        key={child.path || `${keyPrefix}${childIdx}`}
      >
        {renderNested(child.children)}
      </Route>
    )
  })

const App = props => {
  const layoutType = useSelector(state => state.settings.layoutType)
  const currentUserId = useSelector(state => state.auth.currentUser?.id)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  function getLayout() {
    let layoutCls = VerticalLayout

    switch (layoutType) {
      case "horizontal":
        layoutCls = HorizontalLayout
        break
      default:
        layoutCls = VerticalLayout
        break
    }
    return layoutCls
  }

  async function requestPermission() {
    if (!messaging || typeof Notification === "undefined") return
    try {
      const permission = await Notification.requestPermission()
      if (permission === "granted") {
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAP_ID,
        })
        if (token) localStorage.setItem("fcm", token)
      }
    } catch {
      // Firebase messaging is unavailable in this environment.
    }
  }
  useEffect(() => subscribeNetworkStatusToasts(), [])

  useEffect(() => {
    if (!messaging) return undefined

    requestPermission()

    let unsubscribe = () => {}
    try {
      unsubscribe = onMessage(messaging, payload => {
        dispatch(setNotificaiton(true))
        dispatch(bumpNotificationPulse())
        refreshNotificationInbox(dispatch, {
          userId: currentUserId,
          // FCM handler below owns level-up queue/celebrate — avoid inbox race + sound on onboarding.
          skipLevelUpCelebration: true,
        })

        const levelUpNotification = {
          id:
            payload?.messageId ||
            payload?.data?.notification_id ||
            payload?.data?.id ||
            "",
          title: payload?.notification?.title || payload?.data?.title || "",
          message:
            payload?.notification?.body ||
            payload?.data?.message ||
            payload?.data?.body ||
            "",
          data: payload?.data || {},
          meta: payload?.data?.meta,
        }

        const isLevelUp = isLevelUpNotification(levelUpNotification)
        const onOnboarding = isOnboardingPath(window.location.pathname)

        // Onboarding: queue level-up for dashboard; skip toast + animation + sound.
        maybeCelebrateLevelUpFromNotification(levelUpNotification, {
          userId: currentUserId,
        })

        if (isLevelUp && onOnboarding) {
          return
        }

        const url =
          payload?.data?.url ||
          payload?.fcmOptions?.link ||
          payload?.notification?.click_action ||
          ""
        toast(
          <Message
            notification={{
              title: payload?.notification?.title || payload?.data?.title || "",
            }}
          />,
          {
            className: "push-toast-container",
            position: "top-right",
            hideProgressBar: false,
            icon: false,
            closeOnClick: true,
            onClick: () => openNotificationUrl(url, navigate),
          }
        )
      })
    } catch {
      // Firebase messaging is unavailable in this environment.
    }

    return () => {
      if (typeof unsubscribe === "function") unsubscribe()
    }
  }, [currentUserId, dispatch, navigate])

  const Layout = getLayout()

  return (
    <React.Fragment>
      <DocumentTitle>
        <CreateFlagModalProvider>
          <AddOngoingRoleModalProvider>
            <FloatingPlayerProvider>
              <ChatDockProvider>
                <Routes>
                  <Route
                    path="/logout"
                    element={
                      <NonAuthLayout>
                        <Logout />
                      </NonAuthLayout>
                    }
                  />

                  {authRoutes.map((route, idx) => (
                    <Route
                      path={route.path}
                      element={
                        <GuestAuthMiddleware>
                          <NonAuthLayout>{route.component}</NonAuthLayout>
                        </GuestAuthMiddleware>
                      }
                      key={idx}
                      isAuthProtected={false}
                    />
                  ))}

                  {publicRoutes.map((route, idx) => (
                    <Route
                      path={route.path}
                      element={
                        <NonAuthLayout>{route.component}</NonAuthLayout>
                      }
                      key={`public-${idx}`}
                      isAuthProtected={false}
                    >
                      {renderNestedRoutes(route.children, `public-${idx}-`)}
                    </Route>
                  ))}

                  {onboardingRoutes.map((route, idx) => (
                    <Route
                      path={route.path}
                      element={
                        <OnboardingMiddleware>
                          <NonAuthLayout>{route.component}</NonAuthLayout>
                        </OnboardingMiddleware>
                      }
                      key={`onboarding-${idx}`}
                      isAuthProtected={true}
                    />
                  ))}

                  {adminRoutes.map((route, idx) => (
                    <Route
                      path={route.path}
                      element={
                        <Authmiddleware>
                          <AdminMiddleware>
                            <AdminLayout>{route.component}</AdminLayout>
                          </AdminMiddleware>
                        </Authmiddleware>
                      }
                      key={`admin-${idx}`}
                      isAuthProtected={true}
                      exact
                    >
                      {renderNestedRoutes(route.children, `admin-${idx}-`)}
                    </Route>
                  ))}

                  {cityPortalLegacyRoutes.map((route, idx) => (
                    <Route
                      path={route.path}
                      element={
                        <Authmiddleware>
                          <AdminMiddleware>{route.component}</AdminMiddleware>
                        </Authmiddleware>
                      }
                      key={`city-portal-legacy-${idx}`}
                      isAuthProtected={true}
                    />
                  ))}

                  {userRoutes.map((route, idx) => {
                    const isRootRedirect =
                      route.path === "/" || route.path === "*"

                    return (
                      <Route
                        path={route.path}
                        element={
                          <Authmiddleware>
                            <MemberMiddleware>
                              {isRootRedirect ? (
                                <HomeRedirect />
                              ) : (
                                <Layout>{route.component}</Layout>
                              )}
                            </MemberMiddleware>
                          </Authmiddleware>
                        }
                        key={idx}
                        isAuthProtected={true}
                        exact
                      >
                        {renderNestedRoutes(route.children)}
                      </Route>
                    )
                  })}
                </Routes>
              </ChatDockProvider>
            </FloatingPlayerProvider>
          </AddOngoingRoleModalProvider>
        </CreateFlagModalProvider>
      </DocumentTitle>
    </React.Fragment>
  )
}

App.propTypes = {
  layout: PropTypes.any,
}

const mapStateToProps = state => {
  return {
    layout: state.Layout,
  }
}

export default connect(mapStateToProps, null)(App)
